import { Controller } from 'stimulus';

// Use this controller rather than .tooltips class to survive turbolinks js re-render
export default class extends Controller {
  static targets = ['wrapper', 'dialog', 'backdrop'];

  initialize() {
    // event bind for modal open
    document.addEventListener('modal:open', this.openModal.bind(this));
    document.addEventListener('modal:close', this.closeModal.bind(this));

    // close modal when clicking outside of dialog
    $('#modal-backdrop div').click(this.closeModal.bind(this));
  }

  openModal(event) {
    // initialize() binds modal:open on document and never unbinds, so stale
    // instances from previous Turbolinks pages still hear this event. Their
    // detached dialogs have no visible focusables, so their tab trap would
    // swallow every Tab press document-wide.
    if (!this.element.isConnected) return;
    event.preventDefault();
    this._setup();
    this._showDialog();
    this._showBackdrop();
    this._trapFocus();
  }

  closeModal(e) {
    if (e) e.preventDefault();
    this._hideDialog();
    this._hideBackdrop();
    this._teardown();
    this._releaseFocus();
  }

  disconnect() {
    if (this.boundHandleTabKey) {
      document.removeEventListener('keydown', this.boundHandleTabKey);
    }
  }

  // The page behind the backdrop keeps its links and inputs focusable, so
  // without a trap, Tab walks out of the dialog with the focus ring hidden
  // behind the backdrop — Enter then activates whatever invisible element has
  // focus (e.g., a data-remote link that re-renders the modal body and wipes
  // everything typed, or Ctrl+Enter opens it as a plain HTML page in a new tab).
  _trapFocus() {
    this.previouslyFocused = document.activeElement;
    if (!this.boundHandleTabKey) {
      this.boundHandleTabKey = this._handleTabKey.bind(this);
    }
    document.addEventListener('keydown', this.boundHandleTabKey);

    // Most modal templates focus a field right after dispatching modal:open;
    // only pull focus into the dialog if none of them did.
    requestAnimationFrame(() => {
      if (!this.dialogTarget.contains(document.activeElement)) {
        const focusables = this._focusables();
        if (focusables.length) focusables[0].focus();
      }
    });
  }

  _releaseFocus() {
    if (this.boundHandleTabKey) {
      document.removeEventListener('keydown', this.boundHandleTabKey);
    }
    if (this.previouslyFocused && this.previouslyFocused.isConnected) {
      this.previouslyFocused.focus();
    }
    this.previouslyFocused = null;
  }

  _handleTabKey(event) {
    if (event.key !== 'Tab') return;

    // Never intercept Tab unless this instance's modal is open and on the
    // page — a leaked handler must not block tabbing through normal forms.
    if (!this.element.isConnected || this.wrapperTarget.classList.contains('hidden')) {
      document.removeEventListener('keydown', this.boundHandleTabKey);
      return;
    }

    const focusables = this._focusables();
    if (!focusables.length) {
      event.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (!this.dialogTarget.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  _focusables() {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
    return Array.from(this.dialogTarget.querySelectorAll(selector))
      .filter((el) => el.offsetParent !== null);
  }

  _setup() {
    // close drawer
    $('#drawer').addClass('hidden');
    // modal teardown
    this.wrapperTarget.classList.remove('hidden');
    this.wrapperTarget.classList.add('absolute', 'w-full', 'h-screen');
  }

  _teardown() {
    // modal teardown
    this.wrapperTarget.classList.add('hidden');
    this.wrapperTarget.classList.remove('absolute', 'w-full', 'h-screen');
    // A wide dialog (the measurement editor) sets this as it opens; the next
    // modal starts at the usual size.
    this.wrapperTarget.classList.remove('modal--wide');
    // open drawer
    $('#drawer').removeClass('hidden');
  }

  _showDialog() {
    this.dialogTarget.classList.remove('hidden');
    this.dialogTarget.classList.add('opacity-100', 'translate-y-0', 'sm:scale-100');
    this.dialogTarget.classList.remove('opacity-0', 'translate-y-4', 'sm:translate-y-0', 'sm:scale-95');
  }

  _hideDialog() {
    this.dialogTarget.classList.add('opacity-0', 'translate-y-4', 'sm:translate-y-0', 'sm:scale-95');
    this.dialogTarget.classList.remove('opacity-100', 'translate-y-0', 'sm:scale-100');
    this.dialogTarget.classList.add('hidden');
  }

  _showBackdrop() {
    this.backdropTarget.classList.remove('hidden');
    this.backdropTarget.querySelector('div').classList.remove('hidden');
    this.backdropTarget.classList.add('opacity-100');
    this.backdropTarget.classList.remove('opacity-0');
  }

  _hideBackdrop() {
    this.backdropTarget.classList.add('opacity-0');
    this.backdropTarget.classList.remove('opacity-100');
    this.backdropTarget.querySelector('div').classList.add('hidden');
    this.backdropTarget.classList.add('hidden');
  }
}
